/*
 * This code is the intellectual property of Bulb Inc. and other software publishers.
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */

import {
  Component, EventEmitter, Input, Output,
} from '@angular/core';
import { ButtonSize, ButtonStyle } from 'src/aero/aero-button/aero-button.component';
import { ResumeSection } from './section.model';
import { SectionDataAlertService } from './section-data-alert.service';
// eslint-disable-next-line import/no-cycle
import { ResumeComponent } from '../resume.component';
import { TranslationService } from '../../common/translation.service';
import { AeroDialogService } from '../../aero/aero-dialog/aero-dialog.service';

/**
 * Abstract component representing a section on the resume.
 * Includes the accordion and edit buttons and standard formatting.
 */
@Component({
  selector: 'bulb-resume-section',
  templateUrl: './section.component.html',
  standalone: false,
})
export class ResumeSectionComponent {

  /**
   * The name of the section, displayed in the accordion header.
   */
  @Input({ required: true }) sectionName!: string;

  /**
   * The resume section being rendered.
   */
  @Input() resumeSection : ResumeSection;

  /**
   * The resume section being rendered.
   */
  @Input() resumeComponent : ResumeComponent;

  /**
   * Whether the 'edit icon' is displayed in the accordion header.
   */
  @Input() showEditIcon: boolean = false;

  /**
   * Whether the 'delete icon' is displayed in the accordion header.
   */
  @Input() showDeleteIcon: boolean;

  /**
   * Whether the user can edit the resume or not.
   */
  @Input() canEdit: boolean;

  /**
   * Two-way binding for open property indicating whether the section tab is open.
   * Can be used to programmatically open/close the tab, such as when canceling an edit or reacting to an edit event.
   */
  @Output() openChange = new EventEmitter<boolean>();

  @Input()
  get open() {
    return this.openValue;
  }

  set open(value) {
    this.openValue = value;
    if (value === true) {
      this.showContent = true;
    }
    this.openChange.emit(this.openValue);
  }

  private openValue : boolean = false;

  /**
  * Button style of aero button
  */
  public buttonStyle : ButtonStyle = ButtonStyle.TEXT_WITH_ICON;

  /**
   * Button Size of aero button
   */
  public buttonSize : ButtonSize = ButtonSize.LARGER;

  /**
   * Event emitter for when the user clicks on an edit button.
   */
  @Output() onEdit : EventEmitter< any > = new EventEmitter<any>();

  /**
   * Event emitter for when the user closes the accordion tab by clicking the header tab.
   */
  @Output() onClose : EventEmitter< any > = new EventEmitter< any >();

  @Output() onOpen : EventEmitter< any > = new EventEmitter< any >();

  /**
   * Event emitter for when the user clicks on an edit button.
   */
  @Output() onNewEntry : EventEmitter< any > = new EventEmitter<any>();

  /**
   * Whether the 'Add Entry' button is displayed in the view mode.
   */
  @Input() showAddEntryButton: boolean = false;

  /**
   * For handling accordion tab content visibility.
   */
  public showContent: boolean;

  /**
   * Default constructor.
   */
  constructor(
    private sectionDataAlertService : SectionDataAlertService,
    private $translate: TranslationService,
    private aeroDialogService: AeroDialogService,
  ) {}

  /**
   * Handler for when the section tab is open/closed.
   * @param isOpen Whether the section is now open or closed.
   * @param sectionName Section name.
   */
  onSelectedChange(isOpen: boolean, sectionName: string) {
    this.showContent = true;
    // When the tab is opened, if the section is empty, enter edit mode by emitting onEdit event.
    if (isOpen && this.resumeSection.empty) {
      this.onEdit.emit();
    } else if (!isOpen) {
      // Tab is closing, emit onClose event
      this.onClose.emit();
      this.showEditIcon = true;
      // Setting timeout to 250 ms, because default sliding animation has duration of 200ms
      // we want to hide the accordion content after animation is complete.
      setTimeout(() => {
        this.showContent = isOpen;
      }, 250);
    } else if (isOpen) {
      this.onOpen.emit();
      if (sectionName === 'Experience' || sectionName === 'Education') {
        this.showEditIcon = false;
      }
    }
  }

  /**
   * Event handler for edit icon click.
   * @param event Edit click event.
   * @param sectionName Section name.
   */
  onEditIconClick(event, sectionName) {
    if (sectionName === 'Experience' || sectionName === 'Education') {
      this.onSelectedChange(true, sectionName);
      this.showEditIcon = false;
    } else {
      this.onEdit.emit();
    }
    event.stopPropagation();
  }

  /**
   * Add  a new entry for the section.
   *
   * @param event
   */
  addNewEntry(event) {
    this.onNewEntry.emit();
  }

  /**
   * Method that deletes the currently editing resume section.
   */
  public deleteResumeSection(event) : void {
    const detail = this.$translate.translateInstant('resume_custom_section_delete_body');
    const title = this.$translate.translateInstant('resume_custom_section_delete_title');
    this.aeroDialogService.showDialog({
      title,
      detail,
      primaryLabel: this.$translate.translateInstant('button_delete'),
      primaryStyle: this.aeroDialogService.getButtonStyleEnum().NEGATIVE_ACTION,
      icon: 'far fa-trash-alt',
      iconClass: 'aero-alert',
      onPrimary: () => {
        this.removeResumeCustomSection();
      },
    });
    event.stopPropagation();
  }

  /**
   * Helper method to remove the current selected / editing resume section.
   * @private
   */
  private removeResumeCustomSection() : void {
    const index = this.resumeComponent.resume.sections.map((section : ResumeSection) => section.id)
      .indexOf(this.resumeSection.id);
    if (index > -1) {
      this.resumeComponent.resume.sections.splice(index, 1);
      this.resumeComponent.save();
      this.resumeComponent.addComponent(true);
    } else {
      console.error(`Problem removing resume section with id : ${this.resumeSection.id}.`);
    }
  }
}
